import React from "react";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";
import PullQuote from "@/components/brand/PullQuote";

const COMPARISON = [
  { point: "Who they represent", insurer: "The insurance carrier", pa: "The policyholder" },
  { point: "Whose interest comes first", insurer: "The carrier's loss ratio", pa: "Your full claim recovery" },
  { point: "Who pays them", insurer: "The carrier", pa: "Contingency — only if you recover" },
  { point: "Scope of work", insurer: "Estimate the loss per carrier guidelines", pa: "Document, negotiate, recover" },
  { point: "Authority on your file", insurer: "Advisory — they work for the carrier", pa: "Licensed to negotiate and settle" },
];

export default function WhyPublicAdjusters() {
  return (
    <section
      data-testid="why-pa-section"
      className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-gradient"
    >
      <AmbientLight size={1100} color="blue-soft" intensity={0.5} style={{ top: "30%", right: "-20%" }} />
      <AmbientLight size={700} color="gold" intensity={0.18} style={{ bottom: "-5%", left: "10%" }} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <ChapterMark index="04" label="Why Public Adjusters" align="center" className="mb-14" />

        <div className="max-w-4xl mx-auto text-center mb-24">
          <h2 className="font-display-tight text-white text-[clamp(2.5rem,5.6vw,5.25rem)] leading-[0.96] tracking-[-0.045em]">
            Most policyholders never know
            <br />
            <span className="gold-metallic font-display font-light">they had a choice.</span>
          </h2>
          <div className="mx-auto mt-12 h-px w-20 bg-gradient-to-r from-transparent via-gold to-transparent" />
          <p className="mt-12 text-ag-text-secondary text-base lg:text-[1.1rem] leading-[1.75] font-light max-w-2xl mx-auto">
            When damage occurs, the carrier sends their own adjuster. That adjuster works for the
            insurance company. A licensed public adjuster works exclusively for you — and the
            difference shows up in the final recovery.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="grid md:grid-cols-2 gap-4 lg:gap-6 max-w-6xl mx-auto"
          data-testid="why-pa-comparison"
        >
          {/* Carrier side */}
          <div className="glass p-6 sm:p-8 lg:p-14 opacity-80">
            <div className="font-mono text-[0.55rem] sm:text-[0.6rem] tracking-[0.3em] text-ag-text-muted mb-3 sm:mb-4">CARRIER&apos;S ADJUSTER</div>
            <div className="font-display text-xl sm:text-2xl lg:text-3xl text-white/70 font-light mb-2 tracking-[-0.025em] leading-[1.2]">
              Insurance Company Adjuster
            </div>
            <div className="text-xs text-ag-text-muted mb-6 sm:mb-10 font-light italic">Sent by the carrier. Paid by the carrier.</div>
            {COMPARISON.map((row) => (
              <div key={row.point} className="border-t border-white/5 py-4 sm:py-6">
                <div className="font-mono text-[0.55rem] sm:text-[0.6rem] text-ag-text-muted/70 tracking-[0.24em] uppercase mb-2 sm:mb-3">
                  {row.point}
                </div>
                <div className="flex items-start gap-3 text-ag-text-secondary/70 text-[0.88rem] sm:text-[0.95rem] font-light leading-[1.65]">
                  <X className="w-4 h-4 mt-0.5 text-white/20 flex-shrink-0" />
                  <span>{row.insurer}</span>
                </div>
              </div>
            ))}
          </div>

          {/* PA side */}
          <div
            className="glass-strong relative p-6 sm:p-8 lg:p-14"
            style={{ boxShadow: "inset 0 1px 0 rgba(198,168,107,0.2), 0 40px 80px -20px rgba(0,119,255,0.3)" }}
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
            <div className="font-mono text-[0.55rem] sm:text-[0.6rem] tracking-[0.3em] text-gold mb-3 sm:mb-4">AG PUBLIC ADJUSTING</div>
            <div className="font-display text-xl sm:text-2xl lg:text-3xl text-white font-light mb-2 tracking-[-0.025em] leading-[1.2]">
              Licensed Public Adjuster
            </div>
            <div className="text-xs text-gold/70 mb-6 sm:mb-10 font-light italic">Hired by you. Working for you.</div>
            {COMPARISON.map((row) => (
              <div key={row.point} className="border-t border-white/8 py-4 sm:py-6">
                <div className="font-mono text-[0.55rem] sm:text-[0.6rem] text-gold/70 tracking-[0.24em] uppercase mb-2 sm:mb-3">{row.point}</div>
                <div className="flex items-start gap-3 text-white text-[0.88rem] sm:text-[0.95rem] font-light leading-[1.65]">
                  <Check className="w-4 h-4 mt-0.5 text-gold flex-shrink-0" />
                  <span>{row.pa}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="mt-24 lg:mt-32">
          <PullQuote attribution="Florida Statute § 626.854">
            “A policyholder may retain a licensed public adjuster
            <br className="hidden md:block" />
            at any stage of the claim — before, during, or after the carrier’s review.”
          </PullQuote>
        </div>
      </div>
    </section>
  );
}
